import React, { useState } from 'react';
import { ArrowRight, Check, TrendingUp, Users, Banknote, ClipboardCheck } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import './Partners.css';

const benefits = [
  { icon: <TrendingUp />, title: 'Steady Project Pipeline', desc: 'Access a continuous stream of verified, design-ready, financially qualified homeowners. No more unpredictable project flow.' },
  { icon: <Banknote />, title: 'Zero Marketing Spend', desc: 'Sculpit handles all customer acquisition. You focus on what you do best - building quality homes.' },
  { icon: <ClipboardCheck />, title: 'Ready-to-Build Plans', desc: 'Every project comes with complete 3D designs, blueprints, BOQ, and MEP plans. No ambiguity, no rework.' },
  { icon: <Users />, title: 'Secure Payments', desc: 'Milestone-based escrow payments ensure you get paid on time, every time. No more chasing payments.' },
];

const requirements = [
  'RERA registration (active)',
  'Minimum 10 years of operation',
  'ISO quality certification (preferred)',
  'Minimum 50 completed residential projects',
  'Clean legal record',
  'Own equipment and skilled workforce',
  'Willingness to follow Sculpit quality standards',
  'Openness to 15+ quality inspections per project',
];

const process = [
  { step: '01', title: 'Apply', desc: 'Fill out the partner application form with your company details, portfolio, and certifications.' },
  { step: '02', title: 'Verification', desc: 'Our team verifies your RERA registration, past projects, client references, and quality standards.' },
  { step: '03', title: 'Onboarding', desc: 'Complete Sculpit\'s partner training on our platform, quality standards, and reporting requirements.' },
  { step: '04', title: 'Start Building', desc: 'Get matched with your first project. Build with Sculpit\'s support, tracking, and payment infrastructure.' },
];

export default function Partners() {
  const [form, setForm] = useState({ company: '', contact: '', email: '', phone: '', experience: '', projects: '', rera: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you! Our partnerships team will contact you within 48 hours.');
  };

  return (
    <div className="partners-page">
      <section className="partners-hero section-dark">
        <div className="partners-hero__bg">
          <div className="partners-hero__orb partners-hero__orb--1" />
        </div>
        <div className="container partners-hero__content">
          <AnimatedSection>
            <span className="label label-teal">For Construction Companies</span>
            <h1>Grow Your Business <span className="text-gradient">with Sculpit</span></h1>
            <p className="partners-hero__subtitle">
              Join India's fastest-growing construction platform. Get a steady pipeline of design-ready, financially qualified homeowners - without spending a rupee on marketing.
            </p>
          </AnimatedSection>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Why Partner with Us</span>
              <h2>Benefits for Construction Partners</h2>
            </div>
          </AnimatedSection>
          <div className="partners-benefits">
            {benefits.map((b, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="partner-benefit card-premium">
                  <div className="icon-wrap icon-wrap--teal icon-wrap--lg">{b.icon}</div>
                  <h4>{b.title}</h4>
                  <p>{b.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-gray">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <h2>How Sculpit Works for Partners</h2>
              <p>We handle acquisition, design, and payments. You focus on building.</p>
            </div>
          </AnimatedSection>
          <div className="partners-what-we-do">
            <AnimatedSection direction="left">
              <div className="pwd-card">
                <h4 className="text-teal">What Sculpit Handles</h4>
                <ul>
                  {['Customer acquisition & marketing', 'Complete 3D design & planning', 'Cost estimation & BOQ', 'Payment collection & escrow', 'Quality inspection scheduling', 'Customer communication', 'Government approvals support'].map((item, i) => (
                    <li key={i}><Check size={16} className="pwd-check" /> {item}</li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
            <AnimatedSection direction="right">
              <div className="pwd-card">
                <h4 className="text-teal">What You Handle</h4>
                <ul>
                  {['On-site construction execution', 'Workforce management', 'Material procurement', 'Quality workmanship', 'Timeline adherence', 'Safety compliance', 'Site reporting via Sculpit app'].map((item, i) => (
                    <li key={i}><Check size={16} className="pwd-check" /> {item}</li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <h2>Onboarding Process</h2>
            </div>
          </AnimatedSection>
          <div className="partners-process">
            {process.map((p, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <div className="partner-process-step card-premium">
                  <span className="partner-process-step__num">{p.step}</span>
                  <h4>{p.title}</h4>
                  <p>{p.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-gray">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <h2>Partner Requirements</h2>
              <p>We maintain high standards to ensure the best experience for homeowners.</p>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <div className="partners-requirements card-premium">
              <div className="partners-requirements__grid">
                {requirements.map((req, i) => (
                  <div key={i} className="partners-req-item">
                    <Check size={16} className="partners-req-check" /> {req}
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section" id="partner-form">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Get Started</span>
              <h2>Apply to Become a Sculpit Partner</h2>
              <p>Fill out the form below and our partnerships team will reach out within 48 hours.</p>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <form className="partner-form card-premium" onSubmit={handleSubmit}>
              <div className="partner-form__grid">
                <div className="form-group">
                  <label>Company Name *</label>
                  <input type="text" required value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Your construction company name" />
                </div>
                <div className="form-group">
                  <label>Contact Person *</label>
                  <input type="text" required value={form.contact} onChange={e => setForm({...form, contact: e.target.value})} placeholder="Full name" />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="company@example.com" />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91 98765 43210" />
                </div>
                <div className="form-group">
                  <label>Years of Experience *</label>
                  <input type="text" required value={form.experience} onChange={e => setForm({...form, experience: e.target.value})} placeholder="e.g., 15 years" />
                </div>
                <div className="form-group">
                  <label>Projects Completed</label>
                  <input type="text" value={form.projects} onChange={e => setForm({...form, projects: e.target.value})} placeholder="e.g., 100+" />
                </div>
                <div className="form-group">
                  <label>RERA Registration Number</label>
                  <input type="text" value={form.rera} onChange={e => setForm({...form, rera: e.target.value})} placeholder="Your RERA number" />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>Additional Message</label>
                <textarea rows="4" value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Tell us about your company..." />
              </div>
              <button type="submit" className="btn btn-primary btn-lg" style={{ marginTop: '24px' }}>
                Submit Application <ArrowRight size={18} />
              </button>
            </form>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
